/**
 * CommentThread Component
 * Task 5.5: Comment Thread View (Redesigned)
 *
 * Displays a parent comment with all its replies in a threaded view.
 * Uses PDS design tokens for consistent Pantheon branding.
 *
 * Features:
 * - Header with "Comment" title and close button
 * - Parent comment with icon-based actions
 * - Threaded replies in chronological order
 * - Reply count display
 * - Collapse/expand for resolved threads
 * - Always-visible reply input at bottom
 * - Loading state support
 * - Full accessibility support
 *
 * Architecture:
 * - Composes CommentItem for individual comments
 * - Manages collapsed state for resolved threads
 * - Manages reply input state internally
 * - Actions delegated to parent via callbacks
 */

import React, { useState, useCallback } from 'react';
import type {
  Comment,
  TeamMember,
  JiraFormState,
  JiraFormFields,
  JiraProject,
  JiraIssueType,
  JiraTicket,
  JiraConnection,
  JiraError,
  GeneratedTicketContent,
} from '../../lib/types';
import { CommentItem } from './CommentItem';
import { JiraForm } from '../jira/JiraForm';
import { JiraConnectionPrompt } from '../jira/JiraConnectionPrompt';
import { tokens } from '../../styles/tokens';
import { CloseIcon, SendIcon } from '../icons/Icons';

export interface CommentThreadProps {
  parent: Comment;
  replies: Comment[];
  authors: Map<string, TeamMember>;
  currentUserId?: string;
  isLoading?: boolean;
  onClose?: () => void;
  onCreateReply?: (content: string, parentComment: Comment) => Promise<void>;
  onResolve?: (comment: Comment) => void;
  onReopen?: (comment: Comment) => void;
  onEditComment?: (comment: Comment) => void;
  onDeleteComment?: (comment: Comment) => void;
  onCopyLink?: (comment: Comment) => void;
  onMarkAsRead?: (comment: Comment) => void;
  onMarkAsUnread?: (comment: Comment) => void;

  // Jira integration props
  /** Linked Jira ticket for this comment (if exists) */
  linkedTicket?: JiraTicket;
  /** Jira connection status for the site */
  jiraConnection?: JiraConnection;
  /** Available Jira projects */
  jiraProjects?: JiraProject[];
  /** Available Jira issue types */
  jiraIssueTypes?: JiraIssueType[];
  /** Whether Jira metadata is loading */
  isJiraMetadataLoading?: boolean;
  /** Whether Jira ticket is being fetched */
  isLoadingJiraTicket?: boolean;
  /** Callback to generate ticket content with AI */
  onGenerateJiraTicket?: (comment: Comment) => Promise<GeneratedTicketContent>;
  /** Callback to create a Jira ticket */
  onCreateJiraTicket?: (comment: Comment, fields: JiraFormFields) => Promise<JiraTicket>;
  /** Callback when ticket is opened in Jira */
  onOpenJiraTicket?: (ticket: JiraTicket) => void;
  /** Callback to initiate Jira OAuth connection */
  onConnectJira?: () => void;
  /** Callback to fetch Jira projects (lazy load) */
  onFetchJiraProjects?: () => Promise<void>;
  /** Callback to fetch issue types for a project */
  onFetchJiraIssueTypes?: (projectKey: string) => Promise<void>;
}

export const CommentThread: React.FC<CommentThreadProps> = ({
  parent,
  replies,
  authors,
  currentUserId,
  isLoading = false,
  onClose,
  onCreateReply,
  onResolve,
  onReopen,
  onEditComment,
  onDeleteComment,
  onCopyLink,
  onMarkAsRead,
  onMarkAsUnread,
  // Jira props
  linkedTicket,
  jiraConnection,
  jiraProjects = [],
  jiraIssueTypes = [],
  isJiraMetadataLoading = false,
  isLoadingJiraTicket = false,
  onGenerateJiraTicket,
  onCreateJiraTicket,
  onOpenJiraTicket,
  onConnectJira,
  onFetchJiraProjects,
  onFetchJiraIssueTypes: _onFetchJiraIssueTypes, // Reserved for project change handler
}) => {
  const isResolved = parent.status === 'resolved';

  // Helper to get default issue type ID, preferring "Task"
  const getDefaultIssueTypeId = () => {
    if (jiraConnection?.defaultIssueTypeId) return jiraConnection.defaultIssueTypeId;
    const taskType = jiraIssueTypes.find(t => t.name.toLowerCase() === 'task');
    return taskType?.id || jiraIssueTypes[0]?.id || '';
  };

  // Collapsed state for resolved threads
  const [isRepliesCollapsed, setIsRepliesCollapsed] = useState(isResolved);

  // Reply input state
  const [replyContent, setReplyContent] = useState('');

  // Hover state for icon buttons
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  // Jira form state
  const [jiraFormState, setJiraFormState] = useState<JiraFormState>('default');
  const [jiraFields, setJiraFields] = useState<JiraFormFields>({
    summary: '',
    description: '',
    projectKey: jiraConnection?.defaultProjectKey || jiraProjects[0]?.key || '',
    issueTypeId: getDefaultIssueTypeId(),
  });
  const [isManualMode, setIsManualMode] = useState(true);
  const [createdTicketInSession, setCreatedTicketInSession] = useState<JiraTicket | undefined>(undefined);
  const [jiraError, setJiraError] = useState<JiraError | undefined>(undefined);
  const [showConnectionPrompt, setShowConnectionPrompt] = useState(false);

  // Effective ticket: prefer prop (from API) over session-created ticket
  const effectiveTicket = linkedTicket ?? createdTicketInSession;

  const replyCount = replies.length;
  const hasReplies = replyCount > 0;
  const isJiraFormOpen = jiraFormState !== 'default';
  const hasJiraConnection = jiraConnection?.isConnected ?? false;
  const canShowJiraForm = hasJiraConnection && !effectiveTicket && onCreateJiraTicket;
  // Show button if either connected (can create) or has connect callback (can prompt)
  const showJiraButton = (!effectiveTicket) && (onCreateJiraTicket || onConnectJira);

  // Get resolved by name
  const getResolvedByName = (): string | undefined => {
    if (parent.resolvedBy) {
      const resolver = authors.get(parent.resolvedBy);
      return resolver?.name || 'Unknown';
    }
    return undefined;
  };

  const toggleRepliesCollapsed = () => {
    setIsRepliesCollapsed(!isRepliesCollapsed);
  };

  // Reply handlers
  const handleReplySubmit = async () => {
    if (!replyContent.trim() || !onCreateReply) return;

    await onCreateReply(replyContent.trim(), parent);
    setReplyContent('');
  };

  // Jira form handlers
  const handleOpenJiraForm = useCallback(() => {
    // If not connected, show connection prompt instead
    if (!hasJiraConnection) {
      setShowConnectionPrompt(true);
      return;
    }
    if (!canShowJiraForm) return;

    // Lazy load projects when form opens
    if (onFetchJiraProjects) {
      onFetchJiraProjects();
    }

    setJiraFormState('edit');
    setIsManualMode(true);
    setJiraFields({
      summary: '',
      description: '',
      projectKey: jiraConnection?.defaultProjectKey || jiraProjects[0]?.key || '',
      issueTypeId: getDefaultIssueTypeId(),
    });
    setJiraError(undefined);
  }, [hasJiraConnection, canShowJiraForm, jiraConnection, jiraProjects, jiraIssueTypes, onFetchJiraProjects]);

  const handleCloseConnectionPrompt = useCallback(() => {
    setShowConnectionPrompt(false);
  }, []);

  const handleJiraFieldsChange = useCallback((fields: JiraFormFields) => {
    setJiraFields(fields);
  }, []);

  const handleGenerateWithAI = useCallback(async () => {
    if (!onGenerateJiraTicket) return;

    setIsManualMode(false);
    setJiraFormState('loading');
    setJiraError(undefined);

    try {
      const generated = await onGenerateJiraTicket(parent);
      setJiraFields((prev) => ({
        ...prev,
        summary: generated.summary,
        description: generated.description,
      }));
      setJiraFormState('edit');
    } catch (err) {
      setJiraError({
        message: err instanceof Error ? err.message : 'Failed to generate ticket content.',
        isRetryable: true,
      });
      setJiraFormState('error');
    }
  }, [onGenerateJiraTicket, parent]);

  const handleJiraSubmit = useCallback(async () => {
    if (!onCreateJiraTicket) return;

    setJiraFormState('submitting');
    setJiraError(undefined);

    try {
      const ticket = await onCreateJiraTicket(parent, jiraFields);
      setCreatedTicketInSession(ticket);
      setJiraFormState('success');
    } catch (err) {
      setJiraError({
        message: err instanceof Error ? err.message : 'Failed to create ticket.',
        isRetryable: true,
      });
      setJiraFormState('error');
    }
  }, [onCreateJiraTicket, parent, jiraFields]);

  const handleJiraCancel = useCallback(() => {
    setJiraFormState('default');
    setJiraFields({
      summary: '',
      description: '',
      projectKey: jiraConnection?.defaultProjectKey || jiraProjects[0]?.key || '',
      issueTypeId: getDefaultIssueTypeId(),
    });
    setIsManualMode(true);
    setJiraError(undefined);
  }, [jiraConnection, jiraProjects, jiraIssueTypes]);

  const handleJiraRetry = useCallback(() => {
    // Retry based on what failed
    if (isManualMode) {
      // Retry submission
      handleJiraSubmit();
    } else {
      // Retry AI generation
      handleGenerateWithAI();
    }
  }, [isManualMode, handleJiraSubmit, handleGenerateWithAI]);

  const handleOpenTicket = useCallback((ticket: JiraTicket) => {
    if (onOpenJiraTicket) {
      onOpenJiraTicket(ticket);
    } else {
      window.open(ticket.url, '_blank', 'noopener,noreferrer');
    }
  }, [onOpenJiraTicket]);

  // Sort replies chronologically
  const sortedReplies = [...replies].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <section
      data-testid={`comment-thread-${parent.id}`}
      data-multidev-sdk="true"
      data-status={parent.status}
      role="region"
      aria-label="Comment thread"
      style={{
        backgroundColor: tokens.colors.bgDefault,
        borderRadius: tokens.borders.radiusContainer,
        border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '600px',
      }}
    >
      {/* Header - Fixed at top */}
      <header
        style={{
          padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
          borderBottom: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
          backgroundColor: tokens.colors.bgSecondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: tokens.typography.fontSize.m,
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.textDefault,
          }}
        >
          Comment
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => setHoveredButton('close')}
            onMouseLeave={() => setHoveredButton(null)}
            aria-label="Close thread"
            data-testid="close-thread-button"
            style={{
              padding: tokens.spacing['5xs'],
              background: hoveredButton === 'close' ? tokens.colors.gray100 : 'transparent',
              border: 'none',
              borderRadius: tokens.borders.radiusButton,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              width: '28px',
              height: '28px',
              transition: 'background-color 0.15s ease',
            }}
          >
            <CloseIcon size={16} color={tokens.colors.gray400} />
          </button>
        )}
      </header>

      {/* Scrollable content area */}
      <div
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Loading indicator */}
        {isLoading && (
          <div
            role="status"
            aria-label="Loading"
            style={{
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
              backgroundColor: tokens.colors.bgSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing['2xs'],
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: `2px solid ${tokens.colors.primary}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <span style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.textSecondary }}>Loading...</span>
          </div>
        )}

        {/* Parent comment */}
        <div style={{ padding: tokens.spacing.s }}>
          <CommentItem
            comment={parent}
            currentUserId={currentUserId}
            resolvedByName={getResolvedByName()}
            linkedTicket={effectiveTicket}
            isJiraFormOpen={isJiraFormOpen}
            isLoadingJiraTicket={isLoadingJiraTicket}
            disabled={isLoading}
            onResolve={onResolve}
            onReopen={onReopen}
            onEdit={onEditComment}
            onDelete={onDeleteComment}
            onCopyLink={onCopyLink}
            onMarkAsRead={onMarkAsRead}
            onMarkAsUnread={onMarkAsUnread}
            onCreateJiraTicket={showJiraButton ? handleOpenJiraForm : undefined}
            onOpenTicket={handleOpenTicket}
          />
        </div>

        {/* Jira Form - Inline Expansion (moved to appear after parent comment) */}
        {isJiraFormOpen && (
          <div style={{ padding: `0 ${tokens.spacing.s} ${tokens.spacing.s}` }}>
            <JiraForm
              formState={jiraFormState}
              fields={jiraFields}
              projects={jiraProjects}
              issueTypes={jiraIssueTypes}
              isManualMode={isManualMode}
              createdTicket={effectiveTicket}
              error={jiraError}
              isLoadingMetadata={isJiraMetadataLoading}
              onFieldsChange={handleJiraFieldsChange}
              onGenerateWithAI={handleGenerateWithAI}
              onSubmit={handleJiraSubmit}
              onCancel={handleJiraCancel}
              onRetry={handleJiraRetry}
              onOpenInJira={handleOpenTicket}
            />
          </div>
        )}

        {/* Jira Connection Prompt - shown when user tries to create ticket without connection */}
        {showConnectionPrompt && (
          <div style={{ padding: `0 ${tokens.spacing.s} ${tokens.spacing.s}` }}>
            <JiraConnectionPrompt
              isAdmin={true}
              onConnect={() => {
                setShowConnectionPrompt(false);
                if (onConnectJira) onConnectJira();
              }}
              onClose={handleCloseConnectionPrompt}
            />
          </div>
        )}

        {/* Reply count and toggle for resolved threads */}
        {hasReplies && (
          <div
            style={{
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
              backgroundColor: tokens.colors.bgSecondary,
              borderTop: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.textSecondary }}>
              {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </span>

            {isResolved && isRepliesCollapsed && (
              <button
                type="button"
                onClick={toggleRepliesCollapsed}
                aria-label="Show replies"
                style={{
                  padding: `${tokens.spacing['5xs']} ${tokens.spacing['2xs']}`,
                  fontSize: tokens.typography.fontSize.xs,
                  color: tokens.colors.primary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Show replies
              </button>
            )}
          </div>
        )}

        {/* Replies list */}
        {hasReplies && !isRepliesCollapsed && (
          <div style={{ padding: `${tokens.spacing.s} ${tokens.spacing.s} ${tokens.spacing.s}` }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacing['2xs'],
              }}
            >
              {sortedReplies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  currentUserId={currentUserId}
                  onEdit={onEditComment}
                  onDelete={onDeleteComment}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Always-visible reply input - Fixed at bottom */}
      {onCreateReply && (
        <div
          data-testid="reply-input-section"
          style={{
            padding: tokens.spacing.s,
            borderTop: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
            backgroundColor: tokens.colors.bgDefault,
            flexShrink: 0,
          }}
        >
          {/* Reply box container with border */}
          <div
            style={{
              border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
              borderRadius: tokens.borders.radiusInput,
              backgroundColor: tokens.colors.bgDefault,
              overflow: 'hidden',
            }}
          >
            <textarea
              value={replyContent}
              onChange={(e) => {
                setReplyContent(e.target.value);
                // Auto-expand: reset height then set to scrollHeight
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && replyContent.trim()) {
                  e.preventDefault();
                  handleReplySubmit();
                }
              }}
              placeholder="Write a reply..."
              disabled={isLoading}
              aria-label="Reply input"
              data-testid="reply-textarea"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                minHeight: '40px',
                padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
                border: 'none',
                outline: 'none',
                fontSize: tokens.typography.fontSize.s,
                fontFamily: 'inherit',
                lineHeight: tokens.typography.lineHeight.m,
                resize: 'none',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                color: tokens.colors.textDefault,
              }}
            />
            {/* Action row for reply buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: `${tokens.spacing['3xs']} ${tokens.spacing['2xs']}`,
              }}
            >
              <button
                type="button"
                onClick={handleReplySubmit}
                onMouseEnter={() => setHoveredButton('send')}
                onMouseLeave={() => setHoveredButton(null)}
                disabled={!replyContent.trim() || isLoading}
                aria-label="Submit reply"
                data-testid="submit-reply-button"
                title="Send reply (Cmd/Ctrl+Enter)"
                style={{
                  padding: tokens.spacing['5xs'],
                  backgroundColor: (!replyContent.trim() || isLoading) ? 'transparent' : (hoveredButton === 'send' ? tokens.colors.gray100 : 'transparent'),
                  border: 'none',
                  borderRadius: tokens.borders.radiusButton,
                  cursor: !replyContent.trim() || isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  transition: 'background-color 0.15s ease',
                }}
              >
                <SendIcon
                  size={16}
                  color={!replyContent.trim() || isLoading ? tokens.colors.gray300 : tokens.colors.gray400}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
